'use client';

import { ReactNode, useEffect, useState } from 'react';
import { infoClient } from '@/lib/client';
import { useHyperliquidStore } from '@/stores/hyperliquidStore';
import { wsManager } from '@/lib/websocket/WebSocketManager';
import { useAccount } from 'wagmi';
import usePortfolioStore from '@/stores/portfolioStore';

export const BUILDER_CODE = '0xB8Add901DaFBa6ea8716247602842D1651246c97';
export const BUILDER_INIT_FEE = '0.1%';
export const BUILDER_FEE = 25;

export function DataLayout({ children }: { children: ReactNode }) {
  const { address } = useAccount();
  const [isSocketConnected, setIsSocketConnected] = useState(wsManager.isConnected());
  const setMetaAndAssetCtxs = useHyperliquidStore(state => state.setMetaAndAssetCtxs);
  const selectedAssetNumber = useHyperliquidStore(state => state.selectedAssetNumber);
  const metaAndAssetCtxs = useHyperliquidStore(state => state.metaAndAssetCtxs);
  const setPerpClearinghouseState = usePortfolioStore(state => state.setPerpClearinghouseState);
  const setSpotClearinghouseState = usePortfolioStore(state => state.setSpotClearinghouseState);
  const setUserPortfolio = usePortfolioStore(state => state.setUserPortfolio);
  const setUserOpenOrders = usePortfolioStore(state => state.setUserOpenOrders);
  const setSelectedAssetNumber = useHyperliquidStore(state => state.setSelectedAssetNumber);
  const setMaxBuilderFee = usePortfolioStore(state => state.setMaxBuilderFee);

  // Load initial data
  useEffect(() => {
    infoClient.getMetaAndAssetCtxs().then(data => {
      setMetaAndAssetCtxs(data.universe, data.assetCtxs);
    });
  }, []);

  // WebSocket connection monitoring
  useEffect(() => {
    const unsubscribe = wsManager.onConnectionChange(setIsSocketConnected);
    return unsubscribe;
  }, []);

  useEffect(() => {
    const BTCIndex = metaAndAssetCtxs.findIndex(ctx => ctx.universe.name === 'BTC');
    if (BTCIndex >= 0 && selectedAssetNumber === -1) {
      setSelectedAssetNumber(BTCIndex, metaAndAssetCtxs[BTCIndex].assetCtxs);
    }
  }, [metaAndAssetCtxs]);

  // Market subscription
  useEffect(() => {
    if (isSocketConnected && selectedAssetNumber >= 0 && metaAndAssetCtxs[selectedAssetNumber]) {
      const coin = metaAndAssetCtxs[selectedAssetNumber].universe.name;
      wsManager.subscribeToMarket(coin);

      return () => {
        wsManager.unsubscribeFromMarket(coin);
      };
    }
  }, [isSocketConnected, selectedAssetNumber]);

  useEffect(() => {
    if (isSocketConnected && selectedAssetNumber >= 0 && metaAndAssetCtxs[selectedAssetNumber] && address) {
      const coin = metaAndAssetCtxs[selectedAssetNumber].universe.name;
      // wsManager.subscribeToUserCoin(coin, address);

      return () => {
        // wsManager.unsubscribeFromUserCoin(coin, address);
      };
    }
  }, [isSocketConnected, selectedAssetNumber, address]);

  // User subscription
  useEffect(() => {
    if (isSocketConnected && address) {
      wsManager.subscribeToUser(address);

      Promise.all([
        infoClient.getPerpClearinghouseState(address).catch(() => null),
        infoClient.getSpotClearinghouseState(address).catch(() => null),
        infoClient.getUserPortfolio(address).catch(() => null),
        infoClient.getUserOpenOrders(address).catch(() => null),
        infoClient.getMaxBuilderFee(address, BUILDER_CODE).catch(() => null),
      ]).then(([perpState, spotState, portfolio, openOrders, maxBuilderFee]) => {
        if (perpState) {
          setPerpClearinghouseState(perpState);
        }

        if (spotState) {
          setSpotClearinghouseState(spotState);
        }

        if (portfolio) {
          setUserPortfolio(portfolio);
        }

        if (openOrders) {
          setUserOpenOrders(openOrders);
        }

        if (maxBuilderFee) {
          setMaxBuilderFee(maxBuilderFee);
        }
      });

      return () => {
        wsManager.unsubscribeFromUser(address);
      };
    }
  }, [isSocketConnected, address]);

  return <>{children}</>;
}
